import { useState } from "react";

import {
  useBulkRegisterMutation,
  useBulkUpdateMutation,
  useGetBulkRecordStatusQuery,
  useGetOfflineUploadStatusQuery,
  useBulkEventRescheduleMutation,
  useBulkUploadOfflineResponsesMutation,
} from "./uploadMutation";
import { useSelector } from "react-redux";
import { getUserLoginId } from "../auth/selectors";

const useUploadsQueries = () => {
  const [bulkRegister] = useBulkRegisterMutation();
  const [bulkUpdate] = useBulkUpdateMutation();
  const [bulkEventReschedule] = useBulkEventRescheduleMutation();
  const [bulkUploadOfflineResponses] = useBulkUploadOfflineResponsesMutation();
  const userLoginId = useSelector(getUserLoginId);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentUploadType, setCurrentUploadType] = useState(null);

  const { data: bulkStatusData, refetch: refetchBulkStatus } =
    useGetBulkRecordStatusQuery(currentJobId, {
      skip: !currentJobId || currentUploadType === "uploadOfflineResponses",
    });

  const { data: offlineStatusData, refetch: refetchOfflineStatus } =
    useGetOfflineUploadStatusQuery(currentJobId, {
      skip: !currentJobId || currentUploadType !== "uploadOfflineResponses",
    });

  const handleBulkRegister = async (schoolId, formData) => {
    try {
      const response = await bulkRegister({
        schoolId,
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.log("Error in bulk register", error);
      throw error;
    }
  };

  const handleBulkUpdate = async (schoolId, formData) => {
    try {
      const response = await bulkUpdate({
        schoolId,
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.log("Error in bulk update", error);
      throw error;
    }
  };

  const handleGetBulkRecordStatus = (jobId, uploadType) => {
    setCurrentUploadType(uploadType);
    setCurrentJobId(jobId);
    return {
      statusData:
        uploadType === "uploadOfflineResponses"
          ? offlineStatusData
          : bulkStatusData,
      refetchStatus: async () => {
        try {
          const result =
            uploadType === "uploadOfflineResponses"
              ? await refetchOfflineStatus()
              : await refetchBulkStatus();
          return result.data;
        } catch (error) {
          console.log("Error refetching status", error);
          return null;
        }
      },
    };
  };

  const handleBulkEventCreate = async (schoolId, formData) => {
    try {
      console.log("Bulk Event Create:", {
        schoolId,
        formData: Object.fromEntries(formData.entries()),
      });
      // Mock successful response
      return {
        id: "mock-event-create-id",
        jobId: "mock-event-create-job",
      };
    } catch (error) {
      console.log("Error in bulk event create", error);
      throw error;
    }
  };

  const handleBulkEventReschedule = async (schoolId, formData) => {
    try {
      const response = await bulkEventReschedule({
        schoolId,
        uploadedBy: userLoginId,
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.log("Error in bulk event reschedule", error);
      throw error;
    }
  };

  const handleBulkEventDelete = async (schoolId, formData) => {
    try {
      console.log("Bulk Event Delete:", {
        schoolId,
        formData: Object.fromEntries(formData.entries()),
      });
      // Mock successful response
      return {
        id: "mock-event-delete-id",
        jobId: "mock-event-delete-job",
      };
    } catch (error) {
      console.log("Error in bulk event delete", error);
      throw error;
    }
  };

  const handleBulkUploadOfflineResponses = async (schoolId, formData) => {
    try {
      const response = await bulkUploadOfflineResponses({
        schoolId,
        formData,
      });

      if (response?.data?.errors) {
        const error = new Error(response.data.message || "Validation failed");
        error.details = response.data;
        throw error;
      }

      return {
        id: response?.data.filename,
        jobId: response?.data.job_id,
      };
    } catch (error) {
      console.log("Error in bulk upload offline responses", error);
      throw error;
    }
  };

  return {
    handleBulkRegister,
    handleBulkUpdate,
    handleGetBulkRecordStatus,
    handleBulkEventCreate,
    handleBulkEventReschedule,
    handleBulkEventDelete,
    handleBulkUploadOfflineResponses,
  };
};

export default useUploadsQueries;
